
import React, { useState, useEffect } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import WebsiteLinksInput from './components/WebsiteLinksInput';

const PETeacherResumeForm = ({ resumeData, onUpdate, validationErrors = {} }) => {
  const [activeSection, setActiveSection] = useState('contact');
  const [newSkill, setNewSkill] = useState('');
  const [newSoftSkill, setNewSoftSkill] = useState('');
  const [newCategorizedSkill, setNewCategorizedSkill] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [newWebsite, setNewWebsite] = useState("");
  const [newProject, setNewProject] = useState({ title: "", description: "", link: "", year: "", technologies: "" });

  const updateField = (field, data) => {
    onUpdate(prev => ({ ...prev, [field]: data }));
  };

  const handlePersonalChange = (field, value) => {
    onUpdate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        handlePersonalChange('profile_image_url', result.file_url || URL.createObjectURL(file));
      } else {
        // Fallback to local URL if upload fails, but keep existing image if any
        handlePersonalChange('profile_image_url', URL.createObjectURL(file));
      }
    } catch (error) {
      handlePersonalChange('profile_image_url', URL.createObjectURL(file));
    }
    setIsUploading(false);
  };

  const addSkill = (category) => {
    const skillValue = category === 'soft_skills' ? newSoftSkill : newSkill;
    if (skillValue.trim()) {
      const currentSkills = resumeData[category] || [];
      updateField(category, [...currentSkills, skillValue.trim()]);
      if (category === 'soft_skills') {
        setNewSoftSkill("");
      } else {
        setNewSkill("");
      }
    }
  };

  const removeSkill = (category, index) => {
    const currentSkills = resumeData[category] || [];
    updateField(category, currentSkills.filter((_, i) => i !== index));
  };

  const addExperience = () => {
    updateField("work_experience", [...(resumeData.work_experience || []), {
      position: "",
      company: "",
      start_date: "",
      end_date: "",
      responsibilities: [""]
    }]);
  };

  const updateExperience = (index, field, value) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("work_experience", updated);
  };

  const addResponsibility = (expIndex) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[expIndex] = {
      ...updated[expIndex],
      responsibilities: [...(updated[expIndex].responsibilities || []), ""]
    };
    updateField("work_experience", updated);
  };

  const updateResponsibility = (expIndex, pointIndex, value) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[expIndex].responsibilities[pointIndex] = value;
    updateField("work_experience", updated);
  };

  const removeResponsibility = (expIndex, pointIndex) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[expIndex].responsibilities.splice(pointIndex, 1);
    updateField("work_experience", updated);
  };

  // Function to categorize skills from key_skills into structured format
  const categorizeSkills = (keySkills) => {
    if (!Array.isArray(keySkills)) return {};
    
    const categorized = {
      programming_languages: [],
      frameworks: [],
      tools: [],
      ai_ml_technologies: []
    };

    keySkills.forEach(skill => {
      const skillLower = skill.toLowerCase();
      if (skillLower.includes('python') || skillLower.includes('java') || skillLower.includes('javascript') || 
          skillLower.includes('c++') || skillLower.includes('sql') || skillLower.includes('html') || 
          skillLower.includes('css') || skillLower.includes('php') || skillLower.includes('ruby') ||
          skillLower.includes('go') || skillLower.includes('rust') || skillLower.includes('swift') ||
          skillLower.includes('c#') || skillLower.includes('kotlin') || skillLower.includes('scala')) {
        categorized.programming_languages.push(skill);
      } else if (skillLower.includes('react') || skillLower.includes('angular') || skillLower.includes('vue') ||
                 skillLower.includes('node') || skillLower.includes('django') || skillLower.includes('flask') ||
                 skillLower.includes('spring') || skillLower.includes('express') || skillLower.includes('laravel') ||
                 skillLower.includes('asp.net') || skillLower.includes('jquery') || skillLower.includes('bootstrap')) {
        categorized.frameworks.push(skill);
      } else if (skillLower.includes('git') || skillLower.includes('docker') || skillLower.includes('aws') ||
                 skillLower.includes('jenkins') || skillLower.includes('kubernetes') || skillLower.includes('jira') ||
                 skillLower.includes('agile') || skillLower.includes('scrum') || skillLower.includes('devops') ||
                 skillLower.includes('ci/cd') || skillLower.includes('terraform') || skillLower.includes('ansible')) {
        categorized.tools.push(skill);
      } else if (skillLower.includes('ai') || skillLower.includes('ml') || skillLower.includes('tensorflow') ||
                 skillLower.includes('pytorch') || skillLower.includes('scikit') || skillLower.includes('opencv') ||
                 skillLower.includes('deep learning') || skillLower.includes('neural networks') || skillLower.includes('nlp') ||
                 skillLower.includes('computer vision') || skillLower.includes('data science') || skillLower.includes('statistics')) {
        categorized.ai_ml_technologies.push(skill);
      } else {
        // For business/soft skills, add to tools category
        categorized.tools.push(skill);
      }
    });

    return categorized;
  };

  // Get skills data - prioritize structured skills, fallback to categorized key_skills
  // For templates, show empty skills. For uploaded resumes, show the skills data.
  const getSkillsData = () => {
    // If this is a template and no user data has been entered, return empty skills
    if (resumeData.isTemplate !== false && 
        (!resumeData.full_name || resumeData.full_name.trim() === '') &&
        (!resumeData.email || resumeData.email.trim() === '') &&
        (!resumeData.phone || resumeData.phone.trim() === '')) {
      return {
        programming_languages: [],
        frameworks: [],
        tools: [],
        ai_ml_technologies: []
      };
    }
    
    if (resumeData.skills && Object.keys(resumeData.skills).length > 0) {
      return resumeData.skills;
    }
    
    if (resumeData.key_skills && Array.isArray(resumeData.key_skills) && resumeData.key_skills.length > 0) {
      return categorizeSkills(resumeData.key_skills);
    }
    
    return {
      programming_languages: [],
      frameworks: [],
      tools: [],
      ai_ml_technologies: []
    };
  };

  // Auto-convert key_skills to structured skills when component mounts or resumeData changes
  // Only convert if this is NOT a template (i.e., user has uploaded a resume)
  useEffect(() => {
    if (resumeData.key_skills && Array.isArray(resumeData.key_skills) && resumeData.key_skills.length > 0 && 
        (!resumeData.skills || Object.keys(resumeData.skills).length === 0) &&
        resumeData.isTemplate === false) {
      const categorizedSkills = categorizeSkills(resumeData.key_skills);
      updateField('skills', categorizedSkills);
    }
  }, [resumeData.key_skills, resumeData.skills, resumeData.isTemplate]);

  const removeExperience = (index) => {
    updateField("work_experience", resumeData.work_experience.filter((_, i) => i !== index));
  };

  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "",
      institution: "",
      start_date: "",
      end_date: "",
      description: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    updateField("education", resumeData.education.filter((_, i) => i !== index));
  };

  const addLanguage = () => {
    updateField("languages", [...(resumeData.languages || []), ""]);
  };

  const updateLanguage = (index, value) => {
    const updated = [...(resumeData.languages || [])];
    updated[index] = value;
    updateField("languages", updated);
  };

  const removeLanguage = (index) => {
    updateField("languages", resumeData.languages.filter((_, i) => i !== index));
  };

  const addWebsite = () => {
    if (newWebsite.trim()) {
      updateField("websites", [...(resumeData.websites || []), newWebsite.trim()]);
      setNewWebsite("");
    }
  };
  const removeWebsite = (index) => {
    updateField("websites", resumeData.websites.filter((_, i) => i !== index));
  };
  const addProject = () => {
    if (newProject.title.trim()) {
      const technologies = newProject.technologies.split(',').map(t => t.trim()).filter(t => t);
      updateField("projects", [...(resumeData.projects || []), { 
        ...newProject, 
        technologies: technologies,
        tech_stack: technologies // Ensure tech_stack is also initialized
      }]);
      setNewProject({ title: "", description: "", link: "", year: "", technologies: "" });
    }
  };
  const removeProject = (index) => {
    updateField("projects", resumeData.projects.filter((_, i) => i !== index));
  };
  const updateProject = (index, field, value) => {
    const updated = [...(resumeData.projects || [])];
    updated[index] = { ...updated[index], [field]: value };
    
    // Maintain data consistency across templates
    if (field === 'technologies') {
      updated[index].tech_stack = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'tech_stack') {
      updated[index].technologies = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'link') {
      updated[index].github_link = value;
    }
    if (field === 'github_link') {
      updated[index].link = value;
    }
    
    updateField("projects", updated);
  };

  const sections = [
    { id: 'contact', label: 'Contact' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'languages', label: 'Languages' },
    { id: 'projects', label: 'Projects' }
  ];

  return (
    <div className="resume-form" style={{marginTop: 0, paddingTop: 0}}>
      <div className="form-content" style={{marginTop: 0, paddingTop: 0}}>
        <div className="form-header">
          <h2 className="form-title">PE Teacher Resume Editor</h2>
        </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">
          {activeSection === 'contact' && (
            <div className="tab-panel">
              <h3 className="section-title">Contact Information & Profile</h3>
              <div className="form-group">
                <label>Profile Picture</label>
                <div className="flex items-center gap-4">
                  {resumeData?.profile_image_url && (
                    <img src={resumeData.profile_image_url} alt="Profile" className="w-20 h-20 rounded-full object-cover" />
                  )}
                  <label className="upload-btn">
                    {isUploading ? 'Uploading...' : 'Upload Photo'}
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleImageUpload}
                      accept="image/*"
                      disabled={isUploading}
                    />
                  </label>
                </div>
              </div>
              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.full_name ? 'error' : ''}`}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={resumeData?.full_name || ""}
                    onChange={(e) => handlePersonalChange("full_name", e.target.value)}
                    placeholder="Hannah Williams"
                    maxLength="50"
                  />
                  {validationErrors.full_name && <p className="error-message">{validationErrors.full_name}</p>}
                </div>
                <div className={`form-group ${validationErrors.job_title ? 'error' : ''}`}>
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={resumeData?.job_title || ""}
                    onChange={(e) => handlePersonalChange("job_title", e.target.value)}
                    placeholder="Middle School Physical Education Teacher"
                    maxLength="50"
                  />
                  {validationErrors.job_title && <p className="error-message">{validationErrors.job_title}</p>}
                </div>
              </div>
              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.email ? 'error' : ''}`}>
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={resumeData?.email || ""}
                    onChange={(e) => handlePersonalChange("email", e.target.value)}
                    placeholder="support@resumedesign.ai"
                    maxLength="60"
                  />
                  {validationErrors.email && <p className="error-message">{validationErrors.email}</p>}
                </div>
                <div className={`form-group ${validationErrors.phone ? 'error' : ''}`}>
                  <label>Phone Number</label>
                  <input
                    type="text"
                    value={resumeData?.phone || ""}
                    onChange={(e) => handlePersonalChange("phone", e.target.value)}
                    placeholder="(555) 345-6789"
                    maxLength="20"
                  />
                  {validationErrors.phone && <p className="error-message">{validationErrors.phone}</p>}
                </div>
              </div>
              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.location ? 'error' : ''}`}>
                  <label>Location</label>
                  <input
                    type="text"
                    value={resumeData?.location || ""}
                    onChange={(e) => handlePersonalChange("location", e.target.value)}
                    placeholder="New York, US"
                    maxLength="50"
                  />
                  {validationErrors.location && <p className="error-message">{validationErrors.location}</p>}
                </div>
                <div className={`form-group ${validationErrors.linkedin ? 'error' : ''}`}>
                  <label>LinkedIn</label>
                  <input
                    type="text"
                    value={resumeData?.linkedin || ""}
                    onChange={(e) => handlePersonalChange("linkedin", e.target.value)}
                    placeholder="LinkedIn.com"
                    maxLength="50"
                  />
                  {validationErrors.linkedin && <p className="error-message">{validationErrors.linkedin}</p>}
                </div>
              </div>
              <WebsiteLinksInput
                websites={resumeData.websites || []}
                newWebsite={newWebsite}
                setNewWebsite={setNewWebsite}
                onAdd={addWebsite}
                onRemove={removeWebsite}
              />
            </div>
          )}

          {activeSection === 'summary' && (
            <div className="tab-panel">
              <h3 className="section-title">Summary</h3>
              <div className={`form-group ${validationErrors.summary ? 'error' : ''}`}>
                <label>Professional Summary</label>
                <textarea
                  value={resumeData?.summary || ""}
                  onChange={(e) => handlePersonalChange("summary", e.target.value)}
                  placeholder="A dedicated and enthusiastic Physical Education Teacher with 5+ years of experience fostering a love for fitness and teamwork in K-12 students. Proven ability to design engaging curriculum, promote healthy lifestyles, and adapt teaching methods to diverse learning styles and abilities. Committed to creating a positive and inclusive environment where all students can achieve their physical potential."
                  rows="6"
                  maxLength="500"
                ></textarea>
                {validationErrors.summary && <p className="error-message">{validationErrors.summary}</p>}
              </div>
            </div>
          )}

          {activeSection === 'experience' && (
            <div className="tab-panel">
              <h3 className="section-title">Work Experience</h3>
              {(resumeData.work_experience || []).map((exp, expIndex) => (
                <div key={expIndex} className="experience-item">
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors[`work_experience.${expIndex}.position`] ? 'error' : ''}`}>
                      <label>Position</label>
                      <input
                        type="text"
                        value={exp.position || ""}
                        onChange={(e) => updateExperience(expIndex, "position", e.target.value)}
                        placeholder="PE Teacher"
                        maxLength="100"
                      />
                      {validationErrors[`work_experience.${expIndex}.position`] && <p className="error-message">{validationErrors[`work_experience.${expIndex}.position`]}</p>}
                    </div>
                    <div className={`form-group ${validationErrors[`work_experience.${expIndex}.company`] ? 'error' : ''}`}>
                      <label>Company</label>
                      <input
                        type="text"
                        value={exp.company || ""}
                        onChange={(e) => updateExperience(expIndex, "company", e.target.value)}
                        placeholder="Springfield High School"
                        maxLength="100"
                      />
                      {validationErrors[`work_experience.${expIndex}.company`] && <p className="error-message">{validationErrors[`work_experience.${expIndex}.company`]}</p>}
                    </div>
                  </div>
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors[`work_experience.${expIndex}.start_date`] ? 'error' : ''}`}>
                      <label>Start Date</label>
                      <input
                        type="date"
                        value={exp.start_date || ""}
                        onChange={(e) => updateExperience(expIndex, "start_date", e.target.value)}
                      />
                      {validationErrors[`work_experience.${expIndex}.start_date`] && <p className="error-message">{validationErrors[`work_experience.${expIndex}.start_date`]}</p>}
                    </div>
                    <div className={`form-group ${validationErrors[`work_experience.${expIndex}.end_date`] ? 'error' : ''}`}>
                      <label>End Date</label>
                      <input
                        type="date"
                        value={exp.end_date || ""}
                        onChange={(e) => updateExperience(expIndex, "end_date", e.target.value)}
                      />
                      {validationErrors[`work_experience.${expIndex}.end_date`] && <p className="error-message">{validationErrors[`work_experience.${expIndex}.end_date`]}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Responsibilities</label>
                    {(exp.responsibilities || []).map((resp, pointIndex) => (
                      <div key={pointIndex} className={`flex items-center gap-2 mb-2 ${validationErrors[`work_experience.${expIndex}.responsibilities.${pointIndex}`] ? 'error' : ''}`}>
                        <input
                          type="text"
                          value={resp || ""}
                          onChange={(e) => updateResponsibility(expIndex, pointIndex, e.target.value)}
                          placeholder="Developed and implemented a comprehensive physical education curriculum..."
                          maxLength="200"
                        />
                        <button type="button" onClick={() => removeResponsibility(expIndex, pointIndex)} className="remove-btn">
                          &#x2715;
                        </button>
                        {validationErrors[`work_experience.${expIndex}.responsibilities.${pointIndex}`] && <p className="error-message">{validationErrors[`work_experience.${expIndex}.responsibilities.${pointIndex}`]}</p>}
                      </div>
                    ))}
                    <button type="button" onClick={() => addResponsibility(expIndex)} className="add-btn">
                      Add Responsibility
                    </button>
                  </div>
                  <button type="button" onClick={() => removeExperience(expIndex)} className="remove-section-btn">
                    Remove Experience
                  </button>
                </div>
              ))}
              <button type="button" onClick={addExperience} className="add-section-btn">
                Add Experience
              </button>
            </div>
          )}

          {activeSection === 'skills' && (
            <div className="tab-panel">
              <h3 className="section-title">Skills</h3>
              
              {/* Clear All Skills Button */}
              <div className="form-group">
                <label>Clear All Skills</label>
                <button
                  type="button"
                  className="clear-skills-btn"
                  onClick={() => {
                    const clearedSkills = {
                      programming_languages: [],
                      frameworks: [],
                      tools: [],
                      ai_ml_technologies: []
                    };
                    updateField('skills', clearedSkills);
                    // Also clear key_skills if they exist
                    if (resumeData.key_skills) {
                      onUpdate({ ...resumeData, skills: clearedSkills, key_skills: [] });
                    }
                  }}
                  style={{ backgroundColor: '#ff4444', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                >
                  🗑️ Clear All Skills
                </button>
              </div>
              <div className="form-group">
                <label>Hard Skills</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(resumeData.skills || []).map((skill, index) => (
                    <span key={index} className="skill-tag">
                      {skill}
                      <button type="button" onClick={() => removeSkill("skills", index)} className="remove-tag-btn">
                        &#x2715;
                      </button>
                    </span>
                  ))}
                </div>
                <div className={`flex items-center gap-2 ${validationErrors.skills ? 'error' : ''}`}>
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill('skills'); } }}
                    placeholder="e.g., Curriculum Development"
                    maxLength="50"
                  />
                  <button type="button" onClick={() => addSkill("skills")} className="add-btn">
                    Add
                  </button>
                </div>
                {validationErrors.skills && <p className="error-message">{validationErrors.skills}</p>}
              </div>

              <div className="form-group">
                <label>Soft Skills</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(resumeData.soft_skills || []).map((skill, index) => (
                    <span key={index} className="skill-tag">
                      {skill}
                      <button type="button" onClick={() => removeSkill("soft_skills", index)} className="remove-tag-btn">
                        &#x2715;
                      </button>
                    </span>
                  ))}
                </div>
                <div className={`flex items-center gap-2 ${validationErrors.soft_skills ? 'error' : ''}`}>
                  <input
                    type="text"
                    value={newSoftSkill}
                    onChange={(e) => setNewSoftSkill(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill('soft_skills'); } }}
                    placeholder="e.g., Teamwork"
                    maxLength="50"
                  />
                  <button type="button" onClick={() => addSkill("soft_skills")} className="add-btn">
                    Add
                  </button>
                </div>
                {validationErrors.soft_skills && <p className="error-message">{validationErrors.soft_skills}</p>}
              </div>
            </div>
          )}

          {activeSection === 'education' && (
            <div className="tab-panel">
              <h3 className="section-title">Education</h3>
              {(resumeData.education || []).map((edu, eduIndex) => (
                <div key={eduIndex} className="education-item">
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors[`education.${eduIndex}.degree`] ? 'error' : ''}`}>
                      <label>Degree</label>
                      <input
                        type="text"
                        value={edu.degree || ""}
                        onChange={(e) => updateEducation(eduIndex, "degree", e.target.value)}
                        placeholder="B.S. in Physical Education"
                        maxLength="100"
                      />
                      {validationErrors[`education.${eduIndex}.degree`] && <p className="error-message">{validationErrors[`education.${eduIndex}.degree`]}</p>}
                    </div>
                    <div className={`form-group ${validationErrors[`education.${eduIndex}.institution`] ? 'error' : ''}`}>
                      <label>Institution</label>
                      <input
                        type="text"
                        value={edu.institution || ""}
                        onChange={(e) => updateEducation(eduIndex, "institution", e.target.value)}
                        placeholder="State University"
                        maxLength="100"
                      />
                      {validationErrors[`education.${eduIndex}.institution`] && <p className="error-message">{validationErrors[`education.${eduIndex}.institution`]}</p>}
                    </div>
                  </div>
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors[`education.${eduIndex}.start_date`] ? 'error' : ''}`}>
                      <label>Start Date</label>
                      <input
                        type="date"
                        value={edu.start_date || ""}
                        onChange={(e) => updateEducation(eduIndex, "start_date", e.target.value)}
                      />
                      {validationErrors[`education.${eduIndex}.start_date`] && <p className="error-message">{validationErrors[`education.${eduIndex}.start_date`]}</p>}
                    </div>
                    <div className={`form-group ${validationErrors[`education.${eduIndex}.end_date`] ? 'error' : ''}`}>
                      <label>End Date</label>
                      <input
                        type="date"
                        value={edu.end_date || ""}
                        onChange={(e) => updateEducation(eduIndex, "end_date", e.target.value)}
                      />
                      {validationErrors[`education.${eduIndex}.end_date`] && <p className="error-message">{validationErrors[`education.${eduIndex}.end_date`]}</p>}
                    </div>
                  </div>
                  <div className={`form-group ${validationErrors[`education.${eduIndex}.description`] ? 'error' : ''}`}>
                    <label>Description (Optional)</label>
                    <textarea
                      value={edu.description || ""}
                      onChange={(e) => updateEducation(eduIndex, "description", e.target.value)}
                      placeholder="Graduated with honors. Relevant coursework included..."
                      rows="3"
                      maxLength="300"
                    ></textarea>
                    {validationErrors[`education.${eduIndex}.description`] && <p className="error-message">{validationErrors[`education.${eduIndex}.description`]}</p>}
                  </div>
                  <button type="button" onClick={() => removeEducation(eduIndex)} className="remove-section-btn">
                    Remove Education
                  </button>
                </div>
              ))}
              <button type="button" onClick={addEducation} className="add-section-btn">
                Add Education
              </button>
            </div>
          )}

          {activeSection === 'languages' && (
            <div className="tab-panel">
              <h3 className="section-title">Languages</h3>
              <div className="form-group">
                {(resumeData.languages || []).map((lang, index) => (
                  <div key={index} className={`flex items-center gap-2 mb-2 ${validationErrors[`languages.${index}`] ? 'error' : ''}`}>
                    <input
                      type="text"
                      value={lang || ""}
                      onChange={(e) => updateLanguage(index, e.target.value)}
                      placeholder="e.g., Spanish (Fluent)"
                      maxLength="50"
                    />
                    <button type="button" onClick={() => removeLanguage(index)} className="remove-btn">
                      &#x2715;
                    </button>
                    {validationErrors[`languages.${index}`] && <p className="error-message">{validationErrors[`languages.${index}`]}</p>}
                  </div>
                ))}
                <button type="button" onClick={addLanguage} className="add-btn">
                  Add Language
                </button>
                {validationErrors.languages && <p className="error-message">{validationErrors.languages}</p>}
              </div>
            </div>
          )}

          {activeSection === 'projects' && (
            <div className="tab-panel">
              <h3 className="section-title">Projects</h3>
              <div className="form-group">
                <label>Title</label>
                <input type="text" value={newProject.title} onChange={e => setNewProject({ ...newProject, title: e.target.value })} placeholder="Project Title" maxLength="100" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} placeholder="Project Description" rows={2} maxLength={300} />
              </div>
              <div className="form-group">
                <label>Link</label>
                <input type="text" value={newProject.link} onChange={e => setNewProject({ ...newProject, link: e.target.value })} placeholder="https://github.com/yourproject" maxLength="100" />
              </div>
              <div className="form-group">
                <label>Year</label>
                <input type="text" value={newProject.year} onChange={e => setNewProject({ ...newProject, year: e.target.value })} placeholder="2025" maxLength="10" />
              </div>
              <div className="form-group">
                <label>Technologies (comma separated)</label>
                <input type="text" value={newProject.technologies} onChange={e => setNewProject({ ...newProject, technologies: e.target.value })} placeholder="React, Node.js, MongoDB" maxLength="100" />
              </div>
              <button type="button" onClick={addProject} className="add-btn-small">Add Project</button>
              <div className="skills-list flex flex-wrap gap-2" style={{ marginTop: '12px' }}>
                {(resumeData.projects || []).map((proj, idx) => (
                  <div key={idx} className="project-tag" style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '8px', marginBottom: '8px' }}>
                    {proj.title} <br />
                    <span>{proj.description}</span> <br />
                    {proj.link && <a href={proj.link} target="_blank" rel="noopener noreferrer">{proj.link}</a>}<br />
                    {proj.year && <span>Year: {proj.year}</span>}<br />
                    {proj.technologies && proj.technologies.length > 0 && <span>Tech: {proj.technologies.join(', ')}</span>}<br />
                    <button type="button" onClick={() => removeProject(idx)} className="remove-skill-btn">Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PETeacherResumeForm;

           
           